import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate } from "react-router-dom";
import { initNative } from "@/lib/native";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import PageLoader from "@/components/PageLoader";
import { InstallPWA } from "@/components/InstallPWA";

// Eager: public entry points (fast first paint)
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import InviteAccept from "@/pages/InviteAccept";
import SegmentLanding from "@/pages/SegmentLanding";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";

// Lazy: dashboard bundle (code-split for faster initial load)
const NativeAuth = lazy(() => import("@/pages/NativeAuth"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const DeleteAccountInfo = lazy(() => import("@/pages/DeleteAccountInfo"));
const DashboardLayout = lazy(() => import("@/pages/dashboard/DashboardLayout"));
const Overview = lazy(() => import("@/pages/dashboard/Overview"));
const Triage = lazy(() => import("@/pages/dashboard/Triage"));
const Agents = lazy(() => import("@/pages/dashboard/Agents"));
const Chat = lazy(() => import("@/pages/dashboard/Chat"));
const ForgotPassword = lazy(() => import("@/pages/ResetPassword").then((m) => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import("@/pages/ResetPassword").then((m) => ({ default: m.ResetPassword })));
const Insights = lazy(() => import("@/pages/Insights").then((m) => ({ default: m.InsightsIndex })));
const InsightArticle = lazy(() => import("@/pages/Insights").then((m) => ({ default: m.InsightArticle })));
const DeepResearch = lazy(() => import("@/pages/dashboard/DeepResearch"));
const DeepSearch = lazy(() => import("@/pages/dashboard/DeepSearch"));
const LegalTeam = lazy(() => import("@/pages/dashboard/LegalTeam"));
const DraftingStudio = lazy(() => import("@/pages/dashboard/DraftingStudio"));
const DraftWorkspace = lazy(() => import("@/pages/dashboard/DraftWorkspace"));
const IntakePortal = lazy(() => import("@/pages/dashboard/IntakePortal"));
const PublicIntake = lazy(() => import("@/pages/PublicIntake"));
const FlashyCases = lazy(() => import("@/pages/dashboard/FlashyCases"));
const AuthorityGraph = lazy(() => import("@/pages/dashboard/AuthorityGraph"));
const LawAlerts = lazy(() => import("@/pages/dashboard/LawAlerts"));
const Comparison = lazy(() => import("@/pages/dashboard/Comparison"));
const MarketEntry = lazy(() => import("@/pages/dashboard/MarketEntry"));
const Strategy = lazy(() => import("@/pages/dashboard/Strategy"));
const StrategySimulator = lazy(() => import("@/pages/dashboard/StrategySimulator"));
const RiskHeatmap = lazy(() => import("@/pages/dashboard/RiskHeatmap"));
const ExplainAudiences = lazy(() => import("@/pages/dashboard/ExplainAudiences"));
const OpposingCounsel = lazy(() => import("@/pages/dashboard/Adversary").then((m) => ({ default: m.OpposingCounsel })));
const RedTeam = lazy(() => import("@/pages/dashboard/Adversary").then((m) => ({ default: m.RedTeam })));
const MissingEvidence = lazy(() => import("@/pages/dashboard/EvidenceAgents").then((m) => ({ default: m.MissingEvidence })));
const JudgeQuestions = lazy(() => import("@/pages/dashboard/EvidenceAgents").then((m) => ({ default: m.JudgeQuestions })));
const SourceReasoning = lazy(() => import("@/pages/dashboard/EvidenceAgents").then((m) => ({ default: m.SourceReasoning })));
const EvidenceStrength = lazy(() => import("@/pages/dashboard/EvidenceStrength"));
const WinProbability = lazy(() => import("@/pages/dashboard/WinProbability"));
const LawVerification = lazy(() => import("@/pages/dashboard/LawVerification"));
const CrossExamination = lazy(() => import("@/pages/dashboard/CrossExamination"));
const Matters = lazy(() => import("@/pages/dashboard/Matters"));
const MatterDetail = lazy(() => import("@/pages/dashboard/MatterDetail"));
const ContractNegotiator = lazy(() => import("@/pages/dashboard/ContractNegotiator"));
const CostForecast = lazy(() => import("@/pages/dashboard/CostForecast"));
const LitigationPack = lazy(() => import("@/pages/dashboard/LitigationPack"));
const NegotiationSimulator = lazy(() => import("@/pages/dashboard/NegotiationSimulator"));
const MultiAgentTeam = lazy(() => import("@/pages/dashboard/EnterpriseAgents").then((m) => ({ default: m.MultiAgentTeam })));
const AutonomousAgent = lazy(() => import("@/pages/dashboard/EnterpriseAgents").then((m) => ({ default: m.AutonomousAgent })));
const PatternRecognition = lazy(() => import("@/pages/dashboard/EnterpriseAgents").then((m) => ({ default: m.PatternRecognition })));
const Memory = lazy(() => import("@/pages/dashboard/Memory"));
const Compliance = lazy(() => import("@/pages/dashboard/Compliance"));
const Summary = lazy(() => import("@/pages/dashboard/Summary"));
const Jurisdictions = lazy(() => import("@/pages/dashboard/Jurisdictions"));
const Documents = lazy(() => import("@/pages/dashboard/Documents"));
const KnowledgeBase = lazy(() => import("@/pages/dashboard/KnowledgeBase"));
const Billing = lazy(() => import("@/pages/dashboard/Billing"));
const Team = lazy(() => import("@/pages/dashboard/Team"));
const Workspaces = lazy(() => import("@/pages/dashboard/Workspaces"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const MyFirm = lazy(() => import("@/pages/dashboard/MyFirm"));
const Profile = lazy(() => import("@/pages/dashboard/Profile"));
const SavedResearch = lazy(() => import("@/pages/dashboard/SavedResearch"));
const Deadlines = lazy(() => import("@/pages/dashboard/Deadlines"));
const Admin = lazy(() => import("@/pages/dashboard/Admin"));

function NativeBoot() {
  const navigate = useNavigate();
  const { user } = useAuth();
  useEffect(() => { initNative({ navigate, loggedIn: !!user }); }, [navigate, user]);
  return null;
}

function AppRoutes() {
  const location = useLocation();
  // Handle Google OAuth return (session_id in URL fragment) BEFORE anything else.
  // /native-auth is the bridge that hands an app sign-in back to the app, so the web must not consume it.
  if (location.hash?.includes("session_id=") && location.pathname !== "/native-auth") {
    return <AuthCallback />;
  }
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/for/:segment" element={<SegmentLanding />} />
        <Route path="/insights" element={<Insights />} />
        <Route path="/insights/:slug" element={<InsightArticle />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/invite/:token" element={<InviteAccept />} />
        <Route path="/intake/:slug" element={<PublicIntake />} />
        <Route path="/native-auth" element={<NativeAuth />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/delete-account" element={<DeleteAccountInfo />} />
        <Route
          path="/app"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Overview />} />
          <Route path="dashboard" element={<Navigate to="/app" replace />} />
          <Route path="triage" element={<Triage />} />
          <Route path="agents" element={<Agents />} />
          <Route path="assistant" element={<Chat />} />
          <Route path="deep-research" element={<DeepResearch />} />
          <Route path="deep-search" element={<DeepSearch />} />
          <Route path="team-builder" element={<LegalTeam />} />
          <Route path="drafting" element={<DraftingStudio />} />
          <Route path="drafting/:id" element={<DraftWorkspace />} />
          <Route path="intake" element={<IntakePortal />} />
          <Route path="authority-graph" element={<AuthorityGraph />} />
          <Route path="flashy-cases" element={<FlashyCases />} />
          <Route path="alerts" element={<LawAlerts />} />
          <Route path="compare" element={<Comparison />} />
          <Route path="market-entry" element={<MarketEntry />} />
          <Route path="strategy" element={<Strategy />} />
          <Route path="simulator" element={<StrategySimulator />} />
          <Route path="heatmap" element={<RiskHeatmap />} />
          <Route path="explain" element={<ExplainAudiences />} />
          <Route path="opposing" element={<OpposingCounsel />} />
          <Route path="red-team" element={<RedTeam />} />
          <Route path="missing-evidence" element={<MissingEvidence />} />
          <Route path="judge-questions" element={<JudgeQuestions />} />
          <Route path="source-reasoning" element={<SourceReasoning />} />
          <Route path="evidence-strength" element={<EvidenceStrength />} />
          <Route path="win-probability" element={<WinProbability />} />
          <Route path="verify-law" element={<LawVerification />} />
          <Route path="cross-exam" element={<CrossExamination />} />
          <Route path="matters" element={<Matters />} />
          <Route path="matters/:id" element={<MatterDetail />} />
          <Route path="negotiator" element={<ContractNegotiator />} />
          <Route path="cost-forecast" element={<CostForecast />} />
          <Route path="litigation-pack" element={<LitigationPack />} />
          <Route path="negotiate" element={<NegotiationSimulator />} />
          <Route path="multi-agent" element={<MultiAgentTeam />} />
          <Route path="autonomous" element={<AutonomousAgent />} />
          <Route path="patterns" element={<PatternRecognition />} />
          <Route path="memory" element={<Memory />} />
          <Route path="compliance" element={<Compliance />} />
          <Route path="summary" element={<Summary />} />
          <Route path="documents" element={<Documents />} />
          <Route path="knowledge" element={<KnowledgeBase />} />
          <Route path="research" element={<SavedResearch />} />
          <Route path="deadlines" element={<Deadlines />} />
          <Route path="jurisdictions" element={<Jurisdictions />} />
          <Route path="billing" element={<Billing />} />
          <Route path="team" element={<Team />} />
          <Route path="workspaces" element={<Workspaces />} />
          <Route path="firm" element={<MyFirm />} />
          <Route path="profile" element={<Profile />} />
          <Route
            path="admin"
            element={
              <ProtectedRoute adminOnly>
                <Admin />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<NotFound inApp />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
          <NativeBoot />
          <AppRoutes />
          {!window.location.pathname.startsWith("/intake/") && <InstallPWA />}
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
